import React, { useMemo, useState } from 'react';
import { Badge, CopyButton, Tabs } from '../ui';
import { THRESHOLDS, ago, lastValue, peakValue } from '../../observability/contract';
import MetricCard, { replicaColor } from './MetricCard';
import { clock, fmt } from './units';

/* One service's copies and resources over a window: the copies table, then a card per
   metric with its own copy menu, and a control that moves every chart at once. The
   Platform's Services tab draws it under the service's live state (ServicesPanel); the
   load tests' report draws it for each service a run used, over the run's window, with
   its phases drawn (`formatX`, `timeline`). `past`: the window ended before now, so the
   table shows each copy's peak in it (which copy ran hot, where "now" would be the idle
   end of a load test) and the readouts the values at its end. `data` is a
   ServiceMetrics payload (observability/contract.js). Mount it with a `key` per service:
   the copy views start over for another service. */

export const SERVICE_METRICS = [
  { key: 'cpu', title: 'CPU utilization', subtitle: 'share of the task reservation', yMax: 100,
    ref: THRESHOLDS.cpuWarn,
    help: 'Average CPU as a share of what the task reserves, so 100% is the task\'s full allocation, not the host. Fargate throttles a task at its reservation, so a copy that sits near it is the one to scale. The dashed line is the warning threshold behind the health badge.' },
  { key: 'memory', title: 'Memory utilization', subtitle: 'share of the task reservation', yMax: 100,
    ref: THRESHOLDS.memWarn,
    help: 'Memory in use as a share of the task reservation. Unlike CPU it is not throttled: a task that reaches 100% is killed and replaced, so the warning line matters more here. A climb after each deploy that never levels off is a leak.' },
  { key: 'net_rx', title: 'Network in', subtitle: 'bytes received per second' },
  { key: 'net_tx', title: 'Network out', subtitle: 'bytes sent per second' },
  { key: 'requests', title: 'Requests', subtitle: 'per minute, counted by each task',
    help: 'Each task counts the requests it answers, health checks left out, and writes the count to its own log stream once a minute, so the copy view shows how evenly traffic is spread. Pollers such as the bridges make most of their calls outbound and show little here.' },
  { key: 'errors_5xx', title: 'Server errors', subtitle: '5xx responses per bucket' },
];

// A copy's status is its current task's, except STOPPED: no task holds the copy now.
const STATUS_TONE = { RUNNING: 'ok', PENDING: 'warn', STOPPING: 'warn', STOPPED: 'neutral' };
const statusLabel = s => (s === 'STOPPED' ? 'Offline' : s.charAt(0) + s.slice(1).toLowerCase());

function CopyTable({ replicas, metrics, onShow, activeView, past }) {
  const cpu = metrics?.cpu?.series || {};
  const mem = metrics?.memory?.series || {};
  const pct = fmt('percent').value;
  const cpuHead = past ? 'Peak CPU' : 'CPU now';
  const memHead = past ? 'Peak memory' : 'Memory now';
  return (
    <div className="table-container">
      <table className="table table-stack replica-table">
        <thead>
          <tr>
            <th scope="col">Copy</th>
            <th scope="col">Status</th>
            <th scope="col">Since</th>
            <th scope="col">Task</th>
            <th scope="col">Zone</th>
            <th scope="col" className="text-right">{cpuHead}</th>
            <th scope="col" className="text-right">{memHead}</th>
            <th scope="col"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {replicas.map((r) => {
            const offline = r.status === 'STOPPED';
            // An offline copy's last value is from before it stopped, so it is not "now".
            // A past window shows each copy's peak in it, offline or not.
            const c = past ? peakValue(cpu[r.id]) : offline ? null : lastValue(cpu[r.id]);
            const m = past ? peakValue(mem[r.id]) : offline ? null : lastValue(mem[r.id]);
            const since = offline ? r.stopped_at : r.started_at;
            const tasks = r.tasks || [];
            const taskId = r.task_id || tasks[tasks.length - 1]?.id || '';
            return (
              <tr key={r.id} className={activeView === r.id ? 'row-selected' : undefined}>
                <td data-label="Copy">
                  <span className="row gap-2 nowrap">
                    <span className="chart-legend-swatch" style={{ background: replicaColor(r.index) }} aria-hidden="true" />
                    <span className="fw-600">Copy {r.index}</span>
                  </span>
                  {/* More than one task in the window: a deploy, a replacement or a return after a gap. */}
                  {tasks.length > 1 && <span className="cell-sub nowrap">{tasks.length} tasks</span>}
                </td>
                <td data-label="Status">
                  <Badge tone={STATUS_TONE[r.status] || 'warn'} dot>{statusLabel(r.status)}</Badge>
                </td>
                {/* Offline: since its last task stopped. Otherwise: since the current task started. */}
                <td data-label="Since" className="nowrap">
                  {past
                    ? <span className="num">{clock(since)}</span>
                    : <><span className="num">{ago(since)}</span><span className="cell-sub">{clock(since)}</span></>}
                </td>
                <td data-label="Task">
                  <span className="row gap-1 nowrap">
                    <span className={`mono text-xs${offline ? ' text-muted' : ''}`}>{taskId.slice(0, 12)}</span>
                    {taskId && <CopyButton value={taskId} label={`Copy the task id of copy ${r.index}`} />}
                  </span>
                  <span className="cell-sub nowrap">{offline ? 'last task' : 'current task'}</span>
                </td>
                <td data-label="Zone" className="nowrap">{r.zone}</td>
                {/* A copy that peaked at the warning line is the one that ran hot. */}
                <td data-label={cpuHead}
                    className={`text-right num${past && c >= THRESHOLDS.cpuWarn ? ' text-warn fw-600' : ''}`}>
                  {c === null ? '—' : pct(c)}
                </td>
                <td data-label={memHead}
                    className={`text-right num${m >= THRESHOLDS.memWarn ? ' text-warn fw-600' : ''}`}>
                  {m === null ? '—' : pct(m)}
                </td>
                <td data-label="">
                  <button type="button" className="btn btn-ghost btn-sm nowrap" onClick={() => onShow(r.id)}
                          disabled={replicas.length < 2}>
                    Show on charts
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function ServiceDetail({ data, loading, range, formatX, timeline, past = false }) {
  const [views, setViews] = useState({});
  const replicas = useMemo(() => data?.replicas || [], [data]);
  const ids = new Set(replicas.map(r => r.id));
  const viewOf = (key) => {
    const v = views[key] || 'service';
    return v === 'service' || v === 'compare' || ids.has(v) ? v : 'service';
  };
  const setAll = (v) => setViews(Object.fromEntries(SERVICE_METRICS.map(m => [m.key, v])));
  const shared = SERVICE_METRICS.every(m => viewOf(m.key) === viewOf(SERVICE_METRICS[0].key))
    ? viewOf(SERVICE_METRICS[0].key) : null;
  const available = SERVICE_METRICS.filter(m => !data || data.metrics?.[m.key]);
  // A load test's report without the Platform's view of the run (`legacy`): the series
  // its worker kept, one per service, so there are no copies to list or pick.
  const legacy = Boolean(data?.legacy);

  return (
    <>
      {!legacy && (
        <>
          <div className="section-head">
            <h2>Copies</h2>
            <p>every copy that ran in this window; a gap in a copy's line means it was offline</p>
          </div>
          <div className="card card-flush mb-4">
            {loading && !data
              ? <div className="p-4 text-muted">Loading copies…</div>
              : <CopyTable replicas={replicas} metrics={data?.metrics} activeView={shared}
                           onShow={id => setAll(id)} past={past} />}
          </div>
        </>
      )}

      <div className="section-head row-wrap">
        <h2>Resources</h2>
        <p>{legacy ? 'the service as a whole: its copies were not kept' : 'each chart has its own copy menu'}</p>
        {replicas.length > 1 && (
          <div className="ml-auto">
            <Tabs size="sm" label="Show on every chart" value={shared || ''} onChange={setAll}
                  tabs={[
                    { value: 'service', label: 'All' },
                    // The grey dot is the table's "Offline" badge dot: no task holds that copy now.
                    ...replicas.map(r => ({
                      value: r.id,
                      label: r.status === 'STOPPED'
                        ? <><span className="dot health-dot-idle" aria-hidden="true" /><span className="sr-only">Copy </span>#{r.index}<span className="sr-only">, offline</span></>
                        : <><span className="sr-only">Copy </span>#{r.index}</>,
                    })),
                    { value: 'compare', label: 'Compare' },
                  ]} />
          </div>
        )}
      </div>
      <div className="dashboard-grid">
        {available.map(m => (
          <MetricCard
            key={m.key}
            title={m.title}
            subtitle={m.subtitle}
            help={m.help}
            block={data?.metrics?.[m.key]}
            replicas={replicas}
            view={viewOf(m.key)}
            onViewChange={legacy ? undefined : v => setViews(prev => ({ ...prev, [m.key]: v }))}
            range={range}
            yMax={m.yMax}
            refLines={m.ref ? [{ value: m.ref, label: `${m.ref}% warning` }] : undefined}
            loading={loading && !data}
            formatX={formatX}
            timeline={timeline}
            past={past}
          />
        ))}
      </div>
    </>
  );
}
